import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect } from "react";

import { useAppSelector } from "app/hooks";
import ActiveCallWidget from "components/suno-dialer/active-call-widget";
import DialerWidgetContainer from "components/suno-dialer/dialer-widget-container";
import IncomingCallWidget from "components/suno-dialer/incoming-call-widget";
import useSunoDialer from "components/suno-dialer/use-suno-dialer.hook";
import VoiceSettingsWidget from "components/suno-dialer/voice-settings-widget/voice-settings-widget.component";
import {
  NEON_NOW_INTEGRATION,
  SUNO_DIALER_FEATURE,
} from "constants/feature-flags";
import { selectCurrentUser } from "features/auth/auth-slice";
import { dispatchTabSyncMessage } from "hooks/use-sync-between-tabs.hook";
import { useCallContext } from "hooks/use-twilio-call-context";
import { useTwilioContext } from "hooks/use-twilio-context.hook";
import {
  DEFAULT_PATIENTS_QUERY,
  useLazyQueryPatientsQuery,
} from "services/patients";
import { useLogCallEventMutation } from "services/suno-comms/suno-comms-api";

import {
  ANSWERED_STATUS,
  COMPLETED_STATUS,
  INCOMING_CALL_EVENT,
} from "./suno-dialer-constants";

function getCallStatus(event: MessageEvent): string | undefined {
  if (event.data.startsWith("callStatus:")) {
    return String(event.data ?? "").split(":")[1];
  }
  return undefined;
}

export default function SunoDialer() {
  const flags = useFlags();
  const isSunoDialerFeature = flags[SUNO_DIALER_FEATURE];
  const isNeonNowIntegrationEnabled = flags[NEON_NOW_INTEGRATION];
  const currentUser = useAppSelector(selectCurrentUser);
  const [logCallEvent] = useLogCallEventMutation();

  const { setCurrentCall, shouldIgnore } = useCallContext();
  const { twilio } = useTwilioContext();

  const {
    activeCall,
    incomingCall,
    handlePickUpCall,
    handleLoadCallQueue,
    handleDismissCall,
    handleClearIncomingCallFromQueue,
    displaySettings,
  } = useSunoDialer();

  const [queryPatients] = useLazyQueryPatientsQuery();

  useEffect(() => {
    const handleCall = async (event: any) => {
      const callEvent = event.detail;

      const { call } = callEvent;
      const { parameters } = call;
      const phoneNumber = String(parameters?.From ?? "")?.replace(/^\+1/, "");

      if (parameters?.CallSid && shouldIgnore?.(parameters?.CallSid)) {
        return;
      }
      if (
        parameters?.CallSid &&
        phoneNumber &&
        event.type === INCOMING_CALL_EVENT
      ) {
        setCurrentCall(call);
        handleLoadCallQueue([
          {
            id: parameters?.CallSid,
            extra: {
              phone_number: phoneNumber,
            },
          },
        ]);

        dispatchTabSyncMessage({
          action: "ignore_call",
          payload: parameters?.CallSid,
        });

        const { data } = await queryPatients(
          {
            search: phoneNumber || undefined,
            baseFilter: {},
            is_active: true,
            limit: 1,
            query: DEFAULT_PATIENTS_QUERY,
          },
          false,
        );

        if (data && data.length > 0) {
          const patient = data[0];
          const caller =
            patient && patient.phone === phoneNumber ? patient : null;
          handleLoadCallQueue([
            {
              id: parameters?.CallSid,
              callerId: patient?.id,
              extra: {
                avatar_url: caller ? caller.avatar : "",
                full_name: caller ? caller.fullName : "",
                phone_number: caller ? caller.phone : "",
                segment: caller ? caller.crm_segment : "",
                created_at_display: caller ? caller.created_at_display : "",
                gender: caller ? caller.sex : "",
                age_years: caller ? caller.age_years : "",
              },
            },
          ]);
        } else {
          dispatchTabSyncMessage({
            action: "ignore_call",
            payload: parameters?.CallSid,
          });
          handleLoadCallQueue([
            {
              id: parameters?.CallSid,
              extra: {
                phone_number: phoneNumber,
              },
            },
          ]);
        }
        setCurrentCall(call);

        // Log a new call event to Suno Comms API
        const call_data = {
          event: "ringing",
          direction: "incoming",
          status: "ringing",
          callSid: parameters?.CallSid,
          fromNumber: parameters.From.replace(/^\+1/, ""),
          toNumber: parameters?.To.match(/\d+/)[0],
        };
        logCallEvent(call_data);
      }
    };

    window.addEventListener(INCOMING_CALL_EVENT, handleCall);

    // @ts-ignore
    const socket = twilio?.socket;

    socket?.addEventListener("message", (event: MessageEvent) => {
      const callStatus = getCallStatus(event);
      if (callStatus === COMPLETED_STATUS && activeCall) {
        handleDismissCall(activeCall.id);
      } else if (callStatus === COMPLETED_STATUS && incomingCall) {
        handleDismissCall(incomingCall.id);
      } else if (callStatus === ANSWERED_STATUS && !activeCall) {
        // call is picked up by another session
        handleClearIncomingCallFromQueue();
      }
    });

    return () => {
      window.removeEventListener(INCOMING_CALL_EVENT, handleCall);
    };
  }, [
    handleDismissCall,
    handleLoadCallQueue,
    setCurrentCall,
    incomingCall,
    queryPatients,
    handlePickUpCall,
    activeCall,
    twilio?.socket,
    logCallEvent,
    shouldIgnore,
    handleClearIncomingCallFromQueue,
  ]);

  if (
    !isSunoDialerFeature ||
    isNeonNowIntegrationEnabled ||
    !currentUser?.id ||
    currentUser?.id === -1
  ) {
    return null;
  }

  if (displaySettings.showSettings) {
    return <VoiceSettingsWidget />;
  } else if (activeCall) {
    return (
      <DialerWidgetContainer sx={{ maxWidth: "500px" }}>
        <ActiveCallWidget />
      </DialerWidgetContainer>
    );
  } else if (incomingCall) {
    return (
      <DialerWidgetContainer>
        <IncomingCallWidget />
      </DialerWidgetContainer>
    );
  } else {
    return <DialerWidgetContainer />;
  }
}
